import { AppointmentListingModel } from "@common/models/AppointmentListingModel";
import { HospitalScheduleDayEntityModel } from "@common/models/Schedule/HospitalScheduleDayEntityModel";
import { computed, extendObservable, observable, runInAction } from "mobx";
import { HospitalService } from "./hospitalService";
import { EntityScheduleType } from "@common/models/Enums";
import { useLocalObservable } from "mobx-react";
import { DateTime } from "luxon";

/**
 * These are observable hooks to work with service results, current pattern
 * they return a condensed area of concern observable.  For example the appointments for a specific day at a hospital.
 * The hook immediately returns with an empty array and an error and isLoading boolean as an observable object that will work in observers
 * You don't even have to store the hook results in state, useLocalObservable internally already does that.
 * You an just call this hook and put a useEffect on it's results and then you can handle when error is populated, or when the observable 
 * values change and do useful stuff.
 * 
 * No need to call fetch's in useEffect or mnaully put results in state with this pattern, just call the hook.
 */
export class HospitalServiceHooks {
    static useScheduleDayAppointments = (hospitalId: number, dayDate: DateTime) => {
        const localObservable = useLocalObservable(() => {
            const appointmentsForDay = observable<AppointmentListingModel>([]);
            const isLoading = observable.box(true);
            const error = observable.box(undefined);
            HospitalService.getAppointments(hospitalId, dayDate.startOf('day'), dayDate.startOf('day')).then((data) => {
                data.forEach(d => {
                    runInAction(() => {
                        appointmentsForDay.push(d);
                    });
                });
            }).catch((err) => {
                runInAction(() => {
                    appointmentsForDay.clear();
                    error.set(err);
                });
            }).finally(() => {
                runInAction(() => {
                    isLoading.set(false);
                });
            });

            return observable({
                get appointmentsForDay() {
                    return appointmentsForDay;
                },
                get isLoading() {
                    return isLoading.get();
                },
                get error() {
                    return error.get();
                }
            });
        });

        return localObservable;
    }

    static useScheduleEntityAppointments = (hospitalId: number, scheduleEntity: HospitalScheduleDayEntityModel) => {
        const apptResult = HospitalServiceHooks.useScheduleDayAppointments(hospitalId, scheduleEntity.day);
        const obs = useLocalObservable(() => {
            const scheduleDayEntityAppointments = computed(() => {
                return apptResult.appointmentsForDay.filter(a => {
                    if (scheduleEntity.entityType === EntityScheduleType.Veterinarian)
                        return a.veterinarianId === scheduleEntity.entityId;
                    if (scheduleEntity.entityType === EntityScheduleType.Template)
                        return a.hospitalScheduleTemplateId === scheduleEntity.entityId;
                    return false;
                });
            });

            extendObservable(apptResult, {
                get scheduleDayEntityAppointments() {
                    return scheduleDayEntityAppointments.get();
                }
            });
            return apptResult as typeof apptResult & { scheduleDayEntityAppointments: Array<AppointmentListingModel> };
        });
        return obs;
    }
}
